import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Download, Eye, History, Pencil, FolderInput, Trash2, RotateCcw, X, Save } from "lucide-react";
import { API } from "@/portal/AuthContext";
import { appConfirm } from "@/portal/CloudDialogs";

export const fmtBytes = (n) => (!n && n !== 0 ? "–" : n < 1024 ? `${n} B` : n < 1048576 ? `${(n / 1024).toFixed(1)} KB` : n < 1073741824 ? `${(n / 1048576).toFixed(1)} MB` : `${(n / 1073741824).toFixed(2)} GB`);
export const fmtDate = (s) => (s ? new Date(s).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "–");
const PREVIEW = { img: ["jpg", "jpeg", "png", "gif", "webp", "svg", "avif"], pdf: ["pdf"], video: ["mp4", "webm"], audio: ["mp3", "wav", "ogg"] };
export const previewKind = (e) => Object.keys(PREVIEW).find((k) => PREVIEW[k].includes(e)) || null;

export const IconBtn = ({ title, onClick, testId, children, danger }) => (
  <button type="button" title={title} aria-label={title} onClick={onClick} data-testid={testId} className={`p-icon-btn ${danger ? "hover:!text-[#9B2C2C] hover:!bg-[#FDF2F2]" : ""}`}>{children}</button>
);

export const FileRow = ({ f, base, canWrite, onPreview, onRename, onMove, onVersions, onDelete }) => (
  <li className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2.5 border-b border-[#EEEAE0] text-sm" data-testid={`fm-file-${f.id}`}>
    <span className="w-9 h-9 rounded-lg bg-[#F3F0E6] text-[#8C867B] text-[10px] font-semibold grid place-items-center uppercase shrink-0">{f.ext || "file"}</span>
    <button type="button" onClick={() => onPreview(f)} className="font-medium text-[#22201D] hover:text-[#B88E3E] truncate flex-1 min-w-0 basis-[10rem] text-left" data-testid={`fm-name-${f.id}`}>{f.filename}</button>
    <span className="text-xs text-[#8C867B] w-20 text-right">{fmtBytes(f.size)}</span>
    <span className="text-xs text-[#8C867B] w-32 hidden sm:inline">{fmtDate(f.updated_at)}</span>
    <button type="button" onClick={() => onVersions(f)} className="text-[11px] rounded-full border border-[#E5E0D4] px-2 py-0.5 text-[#5C574F] hover:border-[#B88E3E]" data-testid={`fm-versions-${f.id}`} title="Versionen anzeigen"><History size={11} className="inline mr-1" />{f.version_count || 1}</button>
    <span className="flex items-center gap-0.5 ml-auto">
      <a href={`${base}/files/${f.id}/download`} className="p-icon-btn" title="Herunterladen" data-testid={`fm-download-${f.id}`}><Download size={15} /></a>
      <IconBtn title="Vorschau" onClick={() => onPreview(f)} testId={`fm-preview-${f.id}`}><Eye size={15} /></IconBtn>
      {canWrite && <IconBtn title="Umbenennen" onClick={() => onRename(f)} testId={`fm-rename-${f.id}`}><Pencil size={15} /></IconBtn>}
      {canWrite && <IconBtn title="Verschieben" onClick={() => onMove(f)} testId={`fm-move-${f.id}`}><FolderInput size={15} /></IconBtn>}
      {canWrite && <IconBtn title="In den Papierkorb" onClick={() => onDelete(f)} testId={`fm-delete-${f.id}`} danger><Trash2 size={15} /></IconBtn>}
    </span>
  </li>
);

export const UploadQueue = ({ items, onClear }) => items.length === 0 ? null : (
  <div className="p-card p-4 mb-4" data-testid="fm-upload-queue">
    <div className="flex items-center justify-between mb-2"><span className="text-xs font-semibold text-[#22201D]">Hochladen</span><button type="button" onClick={onClear} className="text-xs text-[#8C867B] hover:text-[#22201D]" data-testid="fm-upload-clear">Fertige ausblenden</button></div>
    <ul className="space-y-2">
      {items.map((u) => (
        <li key={u.id} className="text-xs" data-testid={`fm-upload-${u.id}`}>
          <div className="flex justify-between gap-3"><span className="truncate">{u.name}</span><span className={u.status === "error" ? "text-[#9B2C2C]" : "text-[#8C867B]"}>{u.status === "done" ? "Fertig" : u.status === "error" ? u.error : `${u.pct}%`}</span></div>
          <div className="h-1.5 rounded-full bg-[#EEEAE0] mt-1 overflow-hidden"><div className={`h-full rounded-full transition-[width] duration-300 ${u.status === "error" ? "bg-[#D64545]" : "bg-[#B88E3E]"}`} style={{ width: `${u.pct}%` }} /></div>
        </li>
      ))}
    </ul>
  </div>
);

export const Modal = ({ title, onClose, children, testId, wide }) => (
  <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm grid place-items-center p-4" onClick={onClose} data-testid={testId}>
    <div className={`p-card bg-white w-full ${wide ? "max-w-4xl" : "max-w-lg"} max-h-[90vh] overflow-auto p-5`} onClick={(e) => e.stopPropagation()}>
      <div className="flex items-center justify-between mb-4"><h4 className="font-display text-lg text-[#22201D] truncate">{title}</h4><button type="button" onClick={onClose} className="p-icon-btn" aria-label="Schließen" data-testid={`${testId}-close`}><X size={16} /></button></div>
      {children}
    </div>
  </div>
);

export const VersionsPanel = ({ f, base, canWrite, onClose, onChanged }) => {
  const [rows, setRows] = useState(null);
  const load = () => axios.get(`${base}/files/${f.id}/versions`, { withCredentials: true }).then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load(); }, [f.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const restore = async (v) => {
    if (!await appConfirm(`Version ${v.version} als neue aktuelle Fassung übernehmen? Die aktuelle Fassung bleibt als Version erhalten.`)) return;
    try { await axios.post(`${base}/files/${f.id}/versions/${v.id}/restore`, {}, { withCredentials: true }); toast.success("Version wiederhergestellt"); load(); onChanged(); } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
  };
  return (
    <Modal title={`Versionen – ${f.filename}`} onClose={onClose} testId="fm-versions-modal">
      <p className="text-xs text-[#8C867B] mb-3">Es werden die letzten 10 Fassungen aufbewahrt. Ältere werden automatisch entfernt.</p>
      {rows === null ? <p className="text-sm text-[#8C867B]">Lade …</p> : (
        <ul className="divide-y divide-[#EEEAE0]">
          {rows.map((v) => (
            <li key={v.id} className="py-2 flex items-center gap-3 text-sm" data-testid={`fm-version-${v.id}`}>
              <span className="font-semibold w-10">v{v.version}</span><span className="text-xs text-[#8C867B] flex-1">{fmtDate(v.created_at)} · {v.created_by}{v.restored_from ? ` · aus v${v.restored_from}` : ""}{v.edited ? " · im Browser bearbeitet" : ""}</span><span className="text-xs">{fmtBytes(v.size)}</span>
              {v.current ? <span className="text-[10px] rounded-full bg-[#F0F7F2] text-[#2D6A4F] border border-[#C7E5D0] px-2 py-0.5">aktuell</span> : <>
                <a href={`${base}/files/${f.id}/download?version=${v.id}`} className="p-icon-btn" title="Diese Version herunterladen" data-testid={`fm-version-download-${v.id}`}><Download size={14} /></a>
                {canWrite && <IconBtn title="Wiederherstellen" onClick={() => restore(v)} testId={`fm-version-restore-${v.id}`}><RotateCcw size={14} /></IconBtn>}
              </>}
            </li>
          ))}
        </ul>
      )}
    </Modal>
  );
};

export const PreviewModal = ({ f, base, canWrite, onClose, onChanged }) => {
  const kind = previewKind(f.ext);
  const url = `${base}/files/${f.id}/download?inline=1`;
  const [text, setText] = useState(null);
  const [edit, setEdit] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (f.editable) axios.get(`${base}/files/${f.id}/text`, { withCredentials: true }).then((r) => setText(r.data.content)).catch((e) => setText(`⚠ ${e.response?.data?.detail || "Nicht lesbar"}`)); }, [f, base]);
  const save = async () => {
    if (!await appConfirm("Änderungen speichern? Die bisherige Fassung bleibt als Version erhalten.")) return;
    setBusy(true);
    try { await axios.put(`${base}/files/${f.id}/text`, { content: text }, { withCredentials: true }); toast.success("Gespeichert – neue Version angelegt"); setEdit(false); onChanged(); } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); } finally { setBusy(false); }
  };
  return (
    <Modal title={f.filename} onClose={onClose} testId="fm-preview-modal" wide>
      {kind === "img" && <img src={url} alt={f.filename} className="max-h-[70vh] mx-auto rounded-lg" data-testid="fm-preview-img" />}
      {kind === "pdf" && <iframe src={url} title={f.filename} className="w-full h-[70vh] rounded-lg border border-[#EEEAE0]" data-testid="fm-preview-pdf" />}
      {kind === "video" && <video src={url} controls className="max-h-[70vh] mx-auto rounded-lg" data-testid="fm-preview-video" />}
      {kind === "audio" && <audio src={url} controls className="w-full" data-testid="fm-preview-audio" />}
      {f.editable && (
        <div>
          {text === null ? <p className="text-sm text-[#8C867B]">Lade Inhalt …</p> : edit ? <textarea value={text} onChange={(e) => setText(e.target.value)} className="field-input !py-3 font-mono text-xs w-full min-h-[50vh]" data-testid="fm-text-editor" spellCheck={false} /> : <pre className="text-xs bg-[#FAF8F2] border border-[#EEEAE0] rounded-lg p-4 overflow-auto max-h-[60vh] whitespace-pre-wrap" data-testid="fm-text-view">{text}</pre>}
          {canWrite && text !== null && !String(text).startsWith("⚠") && (
            <div className="flex gap-2 mt-3">
              {edit ? <><button type="button" onClick={save} disabled={busy} className="p-btn p-btn-primary !text-xs" data-testid="fm-text-save"><Save size={13} /> Speichern (neue Version)</button><button type="button" onClick={() => setEdit(false)} className="p-btn p-btn-ghost !text-xs" data-testid="fm-text-cancel">Abbrechen</button></> : <button type="button" onClick={() => setEdit(true)} className="p-btn p-btn-secondary !text-xs" data-testid="fm-text-edit"><Pencil size={13} /> Bearbeiten</button>}
            </div>
          )}
        </div>
      )}
      {!kind && !f.editable && <p className="text-sm text-[#5C574F]" data-testid="fm-preview-none">Für diesen Dateityp gibt es keine Vorschau. <a href={`${base}/files/${f.id}/download`} className="underline text-[#B88E3E]">Datei herunterladen</a></p>}
    </Modal>
  );
};

export const MoveDialog = ({ f, folders, onClose, onMove }) => {
  const [target, setTarget] = useState(f.folder || "");
  return (
    <Modal title={`Verschieben – ${f.filename}`} onClose={onClose} testId="fm-move-modal">
      <label className="p-label">Zielordner</label>
      <select value={target} onChange={(e) => setTarget(e.target.value)} className="field-input !py-2.5 text-sm w-full" data-testid="fm-move-target">
        <option value="">/ (Hauptverzeichnis)</option>
        {folders.map((p) => <option key={p} value={p}>/{p}</option>)}
      </select>
      <div className="flex gap-2 mt-4"><button type="button" onClick={() => onMove(target)} className="p-btn p-btn-primary !text-xs" data-testid="fm-move-confirm">Verschieben</button><button type="button" onClick={onClose} className="p-btn p-btn-ghost !text-xs">Abbrechen</button></div>
    </Modal>
  );
};

export const useUploader = (base, folder, reload) => {
  const [queue, setQueue] = useState([]);
  const upd = (id, patch) => setQueue((q) => q.map((u) => (u.id === id ? { ...u, ...patch } : u)));
  const uploadOne = async (file, relFolder, overwrite = false) => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const target = [folder, relFolder].filter(Boolean).join("/");
    setQueue((q) => [...q, { id, name: (relFolder ? relFolder + "/" : "") + file.name, pct: 0, status: "running" }]);
    try {
      let init;
      try {
        init = (await axios.post(`${base}/files/uploads/init`, { filename: file.name, folder: target, size: file.size, content_type: file.type || "application/octet-stream", overwrite }, { withCredentials: true })).data;
      } catch (e) {
        if (e.response?.status === 409 && !overwrite) {
          setQueue((q) => q.filter((u) => u.id !== id));
          if (await appConfirm(`${e.response.data.detail}\n\nJetzt überschreiben?`)) return uploadOne(file, relFolder, true);
          return;
        }
        throw e;
      }
      const parts = Math.max(1, Math.ceil(file.size / init.part_size));
      for (let n = 1; n <= parts; n++) {
        const chunk = file.slice((n - 1) * init.part_size, n * init.part_size);
        await axios.put(`${base}/files/uploads/${init.upload_id}/parts/${n}`, chunk, { withCredentials: true, headers: { "Content-Type": "application/octet-stream" }, onUploadProgress: (ev) => upd(id, { pct: Math.min(99, Math.round((((n - 1) * init.part_size + ev.loaded) / Math.max(file.size, 1)) * 100)) }) });
      }
      await axios.post(`${base}/files/uploads/${init.upload_id}/complete`, {}, { withCredentials: true });
      upd(id, { pct: 100, status: "done" });
      reload();
    } catch (e) {
      upd(id, { status: "error", error: e.response?.data?.detail || "Upload fehlgeschlagen" });
    }
  };
  const addFiles = (fileList, withPaths = false) => {
    Array.from(fileList).forEach((file) => {
      const rel = withPaths && file.webkitRelativePath ? file.webkitRelativePath.split("/").slice(0, -1).join("/") : "";
      uploadOne(file, rel);
    });
  };
  const clear = () => setQueue((q) => q.filter((u) => u.status === "running"));
  return { queue, addFiles, clear };
};

export const useDropFiles = (onFiles) => {
  const ref = useRef(null);
  const [over, setOver] = useState(false);
  const handlers = {
    onDragOver: (e) => { e.preventDefault(); setOver(true); },
    onDragLeave: () => setOver(false),
    onDrop: async (e) => {
      e.preventDefault(); setOver(false);
      const items = Array.from(e.dataTransfer.items || []);
      const out = [];
      const walk = (entry, path) => new Promise((res) => {
        if (entry.isFile) entry.file((file) => { out.push({ file, rel: path }); res(); });
        else if (entry.isDirectory) { const r = entry.createReader(); r.readEntries(async (ents) => { for (const en of ents) await walk(en, path ? `${path}/${entry.name}` : entry.name); res(); }); } else res();
      });
      if (items.length && items[0].webkitGetAsEntry) { for (const it of items) { const en = it.webkitGetAsEntry(); if (en) await walk(en, ""); } onFiles(out); }
      else onFiles(Array.from(e.dataTransfer.files).map((file) => ({ file, rel: "" })));
    },
  };
  return { ref, over, handlers };
};
